import { useState, useCallback } from 'react'
import { supabase } from '../lib/supabase'

export type RateLimitAction = 'login' | 'register' | 'password_reset'

interface RateLimitResult {
  allowed: boolean
  attemptsRemaining: number
  resetAt: string | null
  blockedUntil: string | null
  attemptCount: number
}

interface RateLimitConfig {
  maxAttempts: number
  windowMinutes: number
}

const DEFAULT_LIMITS: Record<RateLimitAction, RateLimitConfig> = {
  login: { maxAttempts: 5, windowMinutes: 15 },
  register: { maxAttempts: 3, windowMinutes: 60 },
  password_reset: { maxAttempts: 3, windowMinutes: 60 }
}

export function useRateLimit() {
  const [checking, setChecking] = useState(false)

  const checkRateLimit = useCallback(async (
    identifier: string,
    action: RateLimitAction
  ): Promise<RateLimitResult> => {
    setChecking(true)

    try {
      const config = DEFAULT_LIMITS[action]

      const { data, error } = await supabase.rpc('check_rate_limit', {
        p_identifier: identifier.toLowerCase(),
        p_action: action,
        p_max_attempts: config.maxAttempts,
        p_window_minutes: config.windowMinutes
      })

      if (error) {
        console.error('Rate limit check error:', error)
        // On error, allow the attempt (fail open for better UX)
        return {
          allowed: true,
          attemptsRemaining: config.maxAttempts,
          resetAt: null,
          blockedUntil: null,
          attemptCount: 0
        }
      }

      return {
        allowed: data.allowed,
        attemptsRemaining: data.attempts_remaining,
        resetAt: data.reset_at,
        blockedUntil: data.blocked_until,
        attemptCount: data.attempt_count
      }
    } finally {
      setChecking(false)
    }
  }, [])

  const recordAttempt = useCallback(async (
    identifier: string,
    action: RateLimitAction,
    success: boolean
  ): Promise<void> => {
    try {
      await supabase.rpc('record_rate_limit_attempt', {
        p_identifier: identifier.toLowerCase(),
        p_action: action,
        p_success: success,
        p_user_agent: navigator.userAgent
      })
    } catch (error) {
      console.error('Failed to record rate limit attempt:', error)
    }
  }, [])

  const formatBlockedMessage = useCallback((blockedUntil: string | null): string => {
    if (!blockedUntil) return ''

    const blockedDate = new Date(blockedUntil)
    const now = new Date()
    const diffMs = blockedDate.getTime() - now.getTime()
    const diffMins = Math.ceil(diffMs / 60000)

    if (diffMins <= 1) return 'Try again in less than a minute'
    if (diffMins < 60) return `Try again in ${diffMins} minutes`

    const diffHours = Math.ceil(diffMins / 60)
    return `Try again in ${diffHours} hour${diffHours > 1 ? 's' : ''}`
  }, [])

  return {
    checking,
    checkRateLimit,
    recordAttempt,
    formatBlockedMessage
  }
}
